import { useEffect, useState } from "react";
import { api } from "../lib/api";

export const formatUkDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

// DB-driven publication / review / update dates for a public path.
export function useContentMeta(path, publishedHint) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    if (!path) return;
    let live = true;
    api.get("/public/content-meta", { params: { path, published: publishedHint || undefined } })
      .then((r) => live && setMeta(r.data))
      .catch(() => live && setMeta(null));
    return () => { live = false; };
  }, [path, publishedHint]);
  return meta;
}

// Keeps datePublished / dateModified in the route's Article JSON-LD in sync with the DB.
export function useArticleDates(meta) {
  useEffect(() => {
    if (!meta) return;
    const el = document.head.querySelector('script[data-route-seo="jsonld"]');
    if (!el) return;
    try {
      const data = JSON.parse(el.textContent);
      const patch = (node) => {
        if (node && node["@type"] === "Article") {
          node.datePublished = meta.published_at;
          node.dateModified = meta.last_updated_at;
        }
        return node;
      };
      el.textContent = JSON.stringify(Array.isArray(data) ? data.map(patch) : patch(data));
    } catch { /* ignore malformed JSON-LD */ }
  }, [meta]);
}

export function ContentDates({ path, publishedHint, className = "", compact = false }) {
  const meta = useContentMeta(path, publishedHint);
  useArticleDates(meta);
  if (!meta) return null;
  const items = [["Published", meta.published_at], ["Last reviewed", meta.last_reviewed_at], ["Last updated", meta.last_updated_at]];
  return (
    <dl className={`flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400 ${className}`} data-testid="content-dates">
      {items.map(([label, value]) => (
        <div key={label} className="flex gap-2">
          <dt className={compact ? "sr-only" : "text-zinc-500"}>{label}:</dt>
          <dd><time dateTime={value}>{compact ? `${label} ${formatUkDate(value)}` : formatUkDate(value)}</time></dd>
        </div>
      ))}
      {meta.version > 1 && <div className="text-zinc-500">v{meta.version}</div>}
    </dl>
  );
}
